import { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useAtomValue, useSetAtom } from 'jotai';

import { cn, useModal } from '@restworld/utils-common';
import { Heading, Button, Modal, ModalContent } from '@restworld/ui-ds-base';
import NextLink from 'next/link';

import {
  isJobApplicationDiscardedAtom,
  jobApplicationAtom,
  jobPositionApiDataAtom,
  jobPositionApplyStatusAtom,
} from '../job-position/job-position.atoms';
import { jobApplicationModalAlreadyDisplayedAtom } from '../worker-entry-point/worker-entry-point.atoms';
import { workerManagedInterviewAtom, workerManagedUsingKlaaryoModalFlagAtom } from '../worker-managed-using-klaaryo';
import { useJobPositionId } from '../../hooks';
import { useSaveResearchModalForJobPosition } from '../worker-entry-point/worker-entry-point-modal';

export function JobAlreadyApplied() {
  const { show, open, close } = useModal();
  const jobPositionApplyStatus = useAtomValue(jobPositionApplyStatusAtom);
  const jobApplicationModalAlreadyDisplayed = useAtomValue(jobApplicationModalAlreadyDisplayedAtom);
  const isJobApplicationDiscarded = useAtomValue(isJobApplicationDiscardedAtom);

  const { t } = useTranslation();
  const { manage_using_klaaryo, ats_applyurl } = useAtomValue(jobPositionApiDataAtom);
  const jobApplication = useAtomValue(jobApplicationAtom);
  const isWorkerManagedModalOpen = useAtomValue(workerManagedUsingKlaaryoModalFlagAtom);
  const setWorkerManagedUsingKlaaryoModalFlagAtom = useSetAtom(workerManagedUsingKlaaryoModalFlagAtom);
  const setWorkerManagedInterview = useSetAtom(workerManagedInterviewAtom);

  useEffect(() => {
    if (jobPositionApplyStatus && !jobApplicationModalAlreadyDisplayed) {
      // The Klaaryo confirmation is already on screen: the worker has just applied and the
      // entry point that opened it has closed, which is what re-runs this effect. The
      // application read at page load is stale here (no channel), and before #500 this branch
      // only re-raised the same flag; opening the plain modal on top would stack two
      // confirmations for one application (QA 15/10/2026).
      if (isWorkerManagedModalOpen) return;
      // The Serena confirmation only where the application says where its interview runs:
      // WhatsApp keeps the WhatsApp step, in-app gets the in-app step (with a way into the
      // chat while it is open). An application with no channel predates the in-app rollout:
      // its interview, if any, happened long ago on WhatsApp, so neither promise is true and
      // it gets the plain "already applied" modal below. ATS positions keep their own screen.
      const channel = jobApplication?.assessment_channel;
      const hasSerenaInterview = channel === 'in_app' || channel === 'whatsapp';
      if (manage_using_klaaryo && !isJobApplicationDiscarded && (hasSerenaInterview || !!ats_applyurl)) {
        setWorkerManagedInterview({
          channel: channel ?? null,
          status: jobApplication?.assessment_status ?? null,
          jobPositionId: jobApplication?.job_position_id,
        });
        setWorkerManagedUsingKlaaryoModalFlagAtom(true);
        return;
      }
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPositionApplyStatus, jobApplicationModalAlreadyDisplayed]);

  return (
    <>
      <Modal open={show} onOpenChange={(v) => !v && close()}>
        <ModalContent className="w-full md:max-w-[26rem] md:min-w-[22rem]" footer={<ModalFooter close={close} />}>
          {/* The icon + title belong in the body (centered), not the ModalContent `title` prop —
              `title` renders in the header row, which pushes the X down beside the title. */}
          <Wrapper className="pt-4 pb-8 gap-4">
            <ModalTitle />
            {isJobApplicationDiscarded ? (
              <Heading size={5} color="neutral-light" align="center">
                {t('job-position-page:application_not_suitable_for_this_offer')}
              </Heading>
            ) : (
              <Heading size={5} color="muted" align="center">
                {t('common:confirm_if_everything_updated')}
              </Heading>
            )}
          </Wrapper>
        </ModalContent>
      </Modal>
    </>
  );
}
export default JobAlreadyApplied;

const ModalTitle = () => {
  const { t } = useTranslation();

  const isJobApplicationDiscarded = useAtomValue(isJobApplicationDiscardedAtom);

  return isJobApplicationDiscarded ? (
    <div className="flex flex-col gap-2 items-center justify-center">
      <InformationCircleIcon className="w-5 h-5 text-purple-700" />
      <Heading size={3} color="neutral" weight="bold" align="center" className="leading-[29.05px] px-3">
        {t('job-position-page:application_was_not_eligible_for_this_offer')}
      </Heading>
    </div>
  ) : (
    <div className="flex flex-col gap-2 items-center justify-center">
      <CheckCircleIcon className="w-13 h-13 text-secondary-900" />
      <Heading size={3} align="center" className="leading-7">
        {t('common:application_delivered')}
      </Heading>
    </div>
  );
};
const ModalFooter = ({ close }: { close?: () => void }) => {
  const { t } = useTranslation();
  const { openSaveResearchModal } = useSaveResearchModalForJobPosition();

  const isJobApplicationDiscarded = useAtomValue(isJobApplicationDiscardedAtom);
  const jobPositionId = useJobPositionId();

  return isJobApplicationDiscarded ? (
    <div className="flex flex-col items-center w-full gap-2">
      <NextLink href="/cerco-lavoro" className="text-primary-800 cursor-pointer w-full">
        <Button fullwidth variant="primary">
          {t('job-position-page:see_other_job_positions')}
        </Button>
      </NextLink>
      <NextLink href={`/candidature/${jobPositionId}`} className="text-primary-800 cursor-pointer w-full">
        <Button fullwidth variant="secondary-neutral" className="text-purple-500">
          {t('job-position-page:discover_how_it_went')}
        </Button>
      </NextLink>
    </div>
  ) : (
    <div className="flex flex-col items-center w-full gap-2">
      <Button
        onClick={() => {
          openSaveResearchModal();
          close();
        }}
        fullwidth
      >
        {t('common:receive_other_offers_similar_to_this')}
      </Button>
      {/* <LinkBase link={'/profilo'} newTab={false} className={'w-full'}>
        <Button fullwidth title={t('common:check_your_profile')} variant="normal" />
      </LinkBase>
      <Button fullwidth title={t('common:close')} variant="secondary-outline" onClick={close} /> */}
    </div>
  );
};
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center', className)} {...props} />
);
